import React, { useCallback, useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import Loading from "../../Common/Loading";

type Country = "Australia" | "India";
type Days = 1 | 2 | 3;

interface ChartRow {
  time: string;
  count: number;
}

interface ApiResponse {
  cols: { id: string; label: string; type: string }[];
  rows: { c: [{ v: string }, { v: number }] }[];
}

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

async function fetchChartData(days: Days, country: Country): Promise<ChartRow[]> {
  const res = await fetch(
    `${API_BASE_URL}api/user-count-graph/chart-data/?days=${days}&country=${country}`
  );
  if (!res.ok) throw new Error("Failed to fetch chart data");
  const data: ApiResponse = await res.json();
  return data.rows.map((row) => ({
    time: row.c[0].v,
    count: row.c[1].v,
  }));
}

// ── Tooltip ───────────────────────────────────────────────────────────────────
interface TooltipProps {
  active?: boolean;
  payload?: { value: number }[];
  label?: string;
}

function formatDisplayLabel(label: string | undefined): string {
  if (!label) return "";
  return label.replace(/^(\d{2}):(\d{2}):(\d{4})/, "$1-$2-$3");
}

function CustomTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #ccc",
        padding: "6px 10px",
        boxShadow: "0 0 5px rgba(0,0,0,0.2)",
        zIndex: 1000,
        pointerEvents: "none",
        fontSize: 13,
        lineHeight: "1.6",
      }}
    >
      <div><strong>Time:</strong> {formatDisplayLabel(label)}</div>
      <div><strong>Active Users Count:</strong> {payload[0].value}</div>
    </div>
  );
}

// ── X-axis tick formatter ─────────────────────────────────────────────────────
function formatAxisTick(value: string): string {
  return value.replace(/^(\d{2}):(\d{2}):(\d{4})/, "$1-$2-$3");
}

// ── Legend ────────────────────────────────────────────────────────────────────
const renderLegend = () => (
  <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 6, paddingRight: 30 }}>
    <div style={{ width: 16, height: 16, background: "#050505", display: "inline-block" }} />
    <span style={{ fontSize: 13 }}>Active Users Count</span>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
export default function EL_UserCountGraph() {
  const [days, setDays]           = useState<Days>(1);
  const [country, setCountry]     = useState<Country>("Australia");
  const [chartData, setChartData] = useState<ChartRow[]>([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchChartData(days, country)
      .then(setChartData)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [days, country]);

  const handleBack = useCallback(() => {
    window.location.href = "/WEBADMIN/REPORTS/EL_ShowCurrentLogins";
  }, []);

  const handleCountryChange = useCallback((c: Country) => {
    setCountry(c);
    setDays(1);
  }, []);

  const handleDaysChange = useCallback((d: Days) => setDays(d), []);

  const dayBtnStyle = (d: Days): React.CSSProperties => ({
    backgroundColor: days === d ? "#0056b3" : "#007bff",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    padding: "10px 20px",
    cursor: "pointer",
    fontSize: 16,
    transition: "background-color 0.3s",
  });

  // Show roughly 30 labels max so they don't overlap.
  // interval=N means show every Nth tick label.
  // 1 day  (~288 points at 5min)  → every 10th  = ~29 labels
  // 2 days (~144 points at 10min) → every 5th   = ~29 labels
  // 3 days (~144 points at 15min) → every 5th   = ~29 labels
  const tickInterval = Math.max(1, Math.floor(chartData.length / 50));

  return (
    <>
      <link
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css"
        rel="stylesheet"
        crossOrigin="anonymous"
      />

      <style>{`
        body {
          font-family: Arial, sans-serif;
          background-color: #f4f4f9;
          margin: 0;
          padding: 0;
        }
        .header {
          background-color: #e8e8ff;
          padding: 10px;
          display: flex;
          justify-content: center;
          align-items: center;
          position: relative;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .header h1 { margin: 0; }
        .btn-back {
          position: absolute;
          left: 0;
          background-color: #007bff;
          color: #fff;
          border: none;
          border-radius: 4px;
          padding: 10px 20px;
          cursor: pointer;
          font-size: 16px;
          transition: background-color 0.3s;
        }
        .btn-back:hover { background-color: #0056b3; }
        .datecls {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 10px;
          padding: 10px;
          background-color: #fff;
          border-radius: 8px;
          box-shadow: 0 0 10px rgba(0,0,0,0.1);
          margin: 20px auto;
          max-width: 800px;
        }
        .graphcls {
          text-align: center;
          margin: 20px auto;
        }
      `}</style>

      <div>
        <div className="header">
          <button className="btn-back" onClick={handleBack}>BACK</button>
          <h1>Active Users Graph</h1>
        </div>

        <div className="datecls">
          <div>
            {(["Australia", "India"] as Country[]).map((c, i) => (
              <label key={c} style={{ marginRight: 8 }}>
                <input
                  type="radio"
                  name="rdoCountry"
                  value={i}
                  checked={country === c}
                  onChange={() => handleCountryChange(c)}
                  style={{ marginRight: 4 }}
                />
                {c}
              </label>
            ))}
          </div>
          <button style={dayBtnStyle(1)} onClick={() => handleDaysChange(1)}>1 Day</button>
          <button style={dayBtnStyle(2)} onClick={() => handleDaysChange(2)}>2 Days</button>
          <button style={dayBtnStyle(3)} onClick={() => handleDaysChange(3)}>3 Days</button>
        </div>

        <div className="container">
          <div className="graphcls">
            {loading && <Loading />}
            {error   && <p style={{ color: "red" }}>{error}</p>}

            {!loading && !error && chartData.length > 0 && (
              <>
                <h4 style={{ textAlign: "left", marginLeft: 80, marginBottom: 4 }}>
                  Active Users Over Time
                </h4>

                <div style={{ overflowX: "auto" }}>
                  <div style={{ width: 1200, height: 800 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={chartData}
                        barSize={2}
                        barCategoryGap="10%"
                        margin={{ top: 10, right: 30, left: 60, bottom: 160 }}
                      >
                        <CartesianGrid strokeDasharray="" stroke="#e0e0e0" />

                        <XAxis
                          dataKey="time"
                          tickFormatter={formatAxisTick}
                          angle={45}
                          textAnchor="start"
                          // show ~30 evenly spaced labels — no overlap on any day setting
                          interval={tickInterval}
                          tick={{ fontSize: 11 }}
                          label={{
                            value: "Time",
                            position: "insideBottom",
                            offset: -140,
                            fontSize: 13,
                          }}
                        />

                        <YAxis
                          label={{
                            value: "Active Users Count",
                            angle: -90,
                            position: "insideLeft",
                            offset: -10,
                            fontSize: 13,
                          }}
                        />

                        <Tooltip
                          content={<CustomTooltip />}
                          cursor={{ fill: "rgba(0,0,0,0.04)" }}
                        />

                        <Legend
                          verticalAlign="top"
                          align="right"
                          content={renderLegend}
                          wrapperStyle={{ paddingBottom: 8 }}
                        />

                        <Bar dataKey="count" fill="#050505" name="Active Users Count" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}